"use client";

import { useState } from "react";
import {
  CipherGrid,
  type CipherMode,
  CipherWorkbench,
  type GridTone,
} from "@/components/tool-kit/cipher-workbench";
import { Checkbox, Notice, TextField } from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  CARD,
  HINT,
  LABEL,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  applyTextOptions,
  type CipherTextOptions,
} from "@/lib/tool-kit/ciphers/alphabet";
import {
  MAX_FACES,
  MIN_FACES,
  padToRod,
  rodLength,
  scytaleCrack,
  scytaleDecode,
  scytaleEncode,
  scytaleGrid,
  stripTurns,
} from "@/lib/tool-kit/ciphers-misc/scytale";

const GRID_COLUMNS = 40;
const CANDIDATES_SHOWN = 8;
const FILLER = "X";
const SPACE_MARK = "␣";
const WHITESPACE = /\s/;
const DEFAULT_OPTIONS: CipherTextOptions = {
  keepCase: true,
  keepSpaces: false,
  keepPunctuation: false,
};

/** English strings. A translated content file overrides these through `ui`. */
export const SCYTALE_CIPHER_STRINGS = {
  mode: "Mode",
  encode: "Encode",
  decode: "Decode",
  plainText: "Plain text",
  cipherText: "Cipher text (the unwound strip)",
  decodedText: "Decoded text",
  inputPlaceholder: "Type or paste your message",
  sample: "I am hurt very badly help",
  faces: "Letters around the rod",
  facesHint:
    "The rod's diameter, counted as the number of lines of text that fit round it ({min}–{max}). It is the key: the strip only reads back with the same number.",
  fill: "Fill the empty cells at the end with X",
  fillHint:
    "A real strip simply stops. Filling makes every turn complete; the X letters stay in the decoded text.",
  keep: "Keep",
  keepCase: "Upper and lower case",
  keepSpaces: "Spaces",
  keepPunctuation: "Punctuation and numbers",
  keepHint:
    "Whatever you keep is written on the strip like a letter; anything else is removed before the message is wound.",
  copy: "Copy result",
  copied: "Copied!",
  clear: "Clear",
  swap: "Swap input and output",
  working: "The rod and the strip",
  gridLabel: "Message on the rod",
  faceLabel: "Face {n}",
  gridIntro:
    "The message is written along the rod, one face after another. The strip touches each face in turn, so it reads the rod one column at a time, top face to bottom. The first column (blue) is the first turn of the strip.",
  gridTruncated:
    "one: Showing the first column.|other: Showing the first {count} columns.",
  rodSize:
    "one: {faces} letter round the rod, {columns} long.|other: {faces} letters round the rod, {columns} long.",
  stripHeading: "The unwound strip",
  stripIntro: "Each group is one turn of the strip, read in order.",
  stripTruncated:
    "one: Showing the first turn.|other: Showing the first {count} turns.",
  emptyWorking: "Type a message to see the rod.",
  tooShort: "A message needs at least two letters to be wound.",
  crackToggle: "Try every rod size",
  crackHeading: "Rod sizes, most English-like first",
  crackHint:
    "Each line decodes the cipher text with a different number of letters round the rod, ranked by common English letter pairs. On a short message the order is a hint, not a verdict.",
  crackUse: "Use {faces}",
  crackFaces: "one: {faces} letter|other: {faces} letters",
} as const;

function visible(char: string | null): string | null {
  if (char === null) {
    return null;
  }
  return WHITESPACE.test(char) ? SPACE_MARK : char;
}

function firstColumnTone(index: number, columns: number): GridTone | undefined {
  return index % columns === 0 && columns > 1 ? "primary" : undefined;
}

export default function ScytaleCipherWidget() {
  const strings = useToolUi(SCYTALE_CIPHER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<CipherMode>("encode");
  const [input, setInput] = useState<string>(strings.sample);
  const [facesText, setFacesText] = useState("4");
  const [options, setOptions] = useState(DEFAULT_OPTIONS);
  const [fill, setFill] = useState(false);
  const [crack, setCrack] = useState(false);

  const parsed = parseIntegerInput(facesText);
  const faces = Math.min(MAX_FACES, Math.max(MIN_FACES, parsed ?? MIN_FACES));
  const prepared = applyTextOptions(input, options);
  const message =
    mode === "encode" && fill ? padToRod(prepared, faces, FILLER) : prepared;
  const output =
    mode === "encode"
      ? scytaleEncode(message, faces)
      : scytaleDecode(prepared, faces);
  const plain = mode === "encode" ? message : output;
  const plainLength = [...plain].length;

  const keyControls = (
    <div className="space-y-4">
      <TextField
        hint={t(strings.facesHint, {
          min: fmt(MIN_FACES),
          max: fmt(MAX_FACES),
        })}
        id="sc-faces"
        label={strings.faces}
        numeric
        onChange={setFacesText}
        value={facesText}
      />
      {mode === "encode" ? (
        <div className="space-y-1">
          <Checkbox checked={fill} label={strings.fill} onChange={setFill} />
          <p className={HINT}>{strings.fillHint}</p>
        </div>
      ) : (
        <Checkbox
          checked={crack}
          label={strings.crackToggle}
          onChange={setCrack}
        />
      )}
    </div>
  );

  const candidates =
    mode === "decode" && crack && plainLength > MIN_FACES
      ? scytaleCrack(prepared).slice(0, CANDIDATES_SHOWN)
      : [];

  const extras =
    candidates.length > 0 ? (
      <section className={`${CARD} space-y-3`}>
        <h2 className={WIDGET_H2}>{strings.crackHeading}</h2>
        <p className={HINT}>{strings.crackHint}</p>
        <ul className="divide-y divide-border">
          {candidates.map((candidate) => (
            <li
              className="flex flex-wrap items-center gap-3 py-2"
              key={candidate.faces}
            >
              <span className="w-24 shrink-0 text-muted-foreground text-xs">
                {tPlural(strings.crackFaces, locale, candidate.faces, {
                  faces: fmt(candidate.faces),
                })}
              </span>
              <span className="min-w-0 flex-1 break-all font-mono text-foreground text-sm">
                {candidate.plain}
              </span>
              <button
                className={BUTTON_SMALL}
                onClick={() => setFacesText(String(candidate.faces))}
                type="button"
              >
                {t(strings.crackUse, { faces: fmt(candidate.faces) })}
              </button>
            </li>
          ))}
        </ul>
      </section>
    ) : null;

  const grid = scytaleGrid(plain, faces);
  const columns = rodLength(plainLength, faces);
  const shownColumns = Math.min(columns, GRID_COLUMNS);
  const cipherText = mode === "encode" ? output : prepared;
  const turns = stripTurns(cipherText, faces);

  const working =
    plainLength >= MIN_FACES ? (
      <>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {strings.gridIntro}
        </p>
        <p className={LABEL}>
          {tPlural(strings.rodSize, locale, faces, {
            faces: fmt(faces),
            columns: fmt(columns),
          })}
        </p>
        <CipherGrid
          cells={grid.flatMap((row) => row.slice(0, shownColumns).map(visible))}
          columns={shownColumns}
          dense
          label={strings.gridLabel}
          rowHeaders={grid.map((_, index) =>
            t(strings.faceLabel, { n: fmt(index + 1) })
          )}
          tone={(index) => firstColumnTone(index, shownColumns)}
        />
        {columns > GRID_COLUMNS ? (
          <p className={HINT}>
            {tPlural(strings.gridTruncated, locale, GRID_COLUMNS, {
              count: fmt(GRID_COLUMNS),
            })}
          </p>
        ) : null}
        <div className="space-y-2">
          <p className={LABEL}>{strings.stripHeading}</p>
          <p className={HINT}>{strings.stripIntro}</p>
          <p className="break-all font-mono text-foreground text-sm">
            {turns
              .slice(0, GRID_COLUMNS)
              .map((turn) => [...turn].map((char) => visible(char)).join(""))
              .join(" ")}
          </p>
          {turns.length > GRID_COLUMNS ? (
            <p className={HINT}>
              {tPlural(strings.stripTruncated, locale, GRID_COLUMNS, {
                count: fmt(GRID_COLUMNS),
              })}
            </p>
          ) : null}
        </div>
      </>
    ) : (
      <Notice>
        {input.length === 0 ? strings.emptyWorking : strings.tooShort}
      </Notice>
    );

  return (
    <CipherWorkbench
      extras={extras}
      idPrefix="sc"
      input={input}
      keyControls={keyControls}
      labels={{
        mode: strings.mode,
        encode: strings.encode,
        decode: strings.decode,
        input: mode === "encode" ? strings.plainText : strings.cipherText,
        inputPlaceholder: strings.inputPlaceholder,
        output: mode === "encode" ? strings.cipherText : strings.decodedText,
        textOptions: strings.keep,
        keepCase: strings.keepCase,
        keepSpaces: strings.keepSpaces,
        keepPunctuation: strings.keepPunctuation,
        copy: strings.copy,
        copied: strings.copied,
        clear: strings.clear,
        swap: strings.swap,
        working: strings.working,
      }}
      mode={mode}
      notices={<p className={HINT}>{strings.keepHint}</p>}
      onInputChange={setInput}
      onModeChange={setMode}
      output={output}
      textOptions={{
        value: options,
        onChange: setOptions,
        show: ["keepCase", "keepSpaces", "keepPunctuation"],
      }}
      working={working}
    />
  );
}
